import type { Dispatch, SetStateAction } from 'react';
import { FormSection } from '@ksyos/design-system';
import { uniqBy } from 'lodash';
import type { IEmbloomQuestionnaire } from '../../../../interfaces/embloomQuestionnaire';
import type { IServiceOrganizationSettingFormSectionProps } from '../ServiceOrganizationSetting';
import { ServiceCode } from '../../../../../../common/enums/serviceCode';
import { translateKey } from '../../../../../translate';
import { ServiceOrganizationSettingFormMode } from '../ServiceOrganizationSetting';
import { EmbloomQuestionnairesFormSectionEdit } from './EmbloomQuestionnairesFormSectionEdit';
import { EmbloomQuestionnairesFormSectionRead } from './EmbloomQuestionnairesFormSectionRead';

interface IEmbloomQuestionnairesFormSectionProps extends IServiceOrganizationSettingFormSectionProps {
    embloomQuestionnaires: { embloomQuestionnaire: IEmbloomQuestionnaire }[];
    loadStandardEmbloomQuestionnaires: () => Promise<void>;
    setEmbloomQuestionnaires: Dispatch<SetStateAction<{ embloomQuestionnaire: IEmbloomQuestionnaire }[]>>;
    setShowAddEmbloomQuestionnaireModal: Dispatch<SetStateAction<boolean>>;
    showAddEmbloomQuestionnaireModal: boolean;
}

export function EmbloomQuestionnairesFormSection(props: IEmbloomQuestionnairesFormSectionProps) {
    if (
        (props.mode === ServiceOrganizationSettingFormMode.read && props.embloomQuestionnaires.length === 0) ||
        props.service.code !== ServiceCode.GGZOembl
    ) {
        return null;
    }

    return (
        <FormSection title={translateKey('embloomQuestionnaires.name')}>
            {props.mode === ServiceOrganizationSettingFormMode.read ? (
                <EmbloomQuestionnairesFormSectionRead
                    decisionSupportEmbloomQuestionnaires={getDecisionSupportEmbloomQuestionnaires()}
                    nonDecisionSupportEmbloomQuestionnaires={getNonDecisionSupportEmbloomQuestionnaires()}
                />
            ) : (
                <EmbloomQuestionnairesFormSectionEdit
                    decisionSupportEmbloomQuestionnaires={getDecisionSupportEmbloomQuestionnaires()}
                    embloomQuestionnaires={props.embloomQuestionnaires}
                    loadStandardEmbloomQuestionnaires={() => props.loadStandardEmbloomQuestionnaires()}
                    nonDecisionSupportEmbloomQuestionnaires={getNonDecisionSupportEmbloomQuestionnaires()}
                    onSave={onSave}
                    setEmbloomQuestionnaires={props.setEmbloomQuestionnaires}
                    setShowAddEmbloomQuestionnaireModal={props.setShowAddEmbloomQuestionnaireModal}
                    showAddEmbloomQuestionnaireModal={props.showAddEmbloomQuestionnaireModal}
                />
            )}
        </FormSection>
    );

    async function onSave(questionnaires: IEmbloomQuestionnaire[]) {
        props.setEmbloomQuestionnaires(
            uniqBy(
                [
                    ...props.embloomQuestionnaires,
                    ...questionnaires.map((questionnaire) => ({ embloomQuestionnaire: questionnaire })),
                ],
                'embloomQuestionnaire.id'
            )
        );
        props.setShowAddEmbloomQuestionnaireModal(false);
    }

    function getDecisionSupportEmbloomQuestionnaires() {
        return props.embloomQuestionnaires.filter((item) => item.embloomQuestionnaire.isDecisionSupport === true);
    }

    function getNonDecisionSupportEmbloomQuestionnaires() {
        return props.embloomQuestionnaires.filter((item) => item.embloomQuestionnaire.isDecisionSupport === false);
    }
}
